import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/contexts/ProfileContext";

export interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  key_skills: string[];
  work_presence: string;
  work_arrangement: string;
  salary_range_min?: number;
  salary_range_max?: number;
  salary_currency: string;
  industry: string;
  custom_industry?: string;
  seniority?: string;
  created_at: string;
  status?: string;
  applicants_count?: number;
  organization: {
    id: string;
    organization?: string;
    full_name?: string;
    logo_url?: string;
  };
}

export function useJobs(
  showUserTests: boolean = false, 
  filters: {
    searchQuery?: string;
    locationFilter?: string;
    skillFilter?: string;
    industryFilter?: string;
    workArrangementFilter?: string;
    salaryRange?: [number, number]; // We'll only use the minimum value
    page?: number;
    pageSize?: number;
  } = {}
) {
  const { user } = useAuth();
  const { profile } = useProfile();

  const {
    searchQuery = '',
    locationFilter = 'all',
    skillFilter = 'all', 
    industryFilter = 'all',
    workArrangementFilter = 'all',
    salaryRange = [0, 200000], // Default values
    page = 1,
    pageSize = 9
  } = filters;

  const { data: jobsResponse, isLoading, error } = useQuery({
    queryKey: [
      'jobs', 
      showUserTests, 
      profile?.id, 
      searchQuery, 
      locationFilter, 
      skillFilter, 
      industryFilter, 
      workArrangementFilter,
      salaryRange[0], // Only include min salary in the query key
      page,
      pageSize
    ],
    queryFn: async () => {
      console.log('Fetching jobs with advanced filtering:', filters);
      console.log('[useJobs] showUserTests =', showUserTests, 'profile.role =', profile?.role);
      
      try {
        // Calculate pagination range
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        let query = supabase
          .from('tests_with_counts')
          .select('*', { count: 'exact' });

        // Profile-based filtering
        if (profile?.role === 'employer' && showUserTests && profile.id) {
          console.log('[useJobs] Showing employer tests for profile:', profile.id);
          query = query.eq('creator_id', profile.id);
        } else {
          // For public jobs board, only show published tests that are marked for job board
          console.log('[useJobs] Showing public jobs with publish_to_job_board = true');
          query = query
            .eq('status', 'published')
            .eq('publish_to_job_board', true);
        }

        // Dynamic filtering
        if (searchQuery) {
          query = query.or(
            `title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%,key_skills.cs.{${searchQuery}}`
          );
        }

        if (locationFilter !== 'all') {
          query = query.eq('location', locationFilter);
        }

        if (industryFilter !== 'all') {
          query = query.eq('industry', industryFilter);
        }

        if (workArrangementFilter !== 'all') {
          query = query.eq('work_arrangement', workArrangementFilter);
        }

        // Skill filtering (more complex due to array)
        if (skillFilter !== 'all') {
          query = query.contains('key_skills', [skillFilter]);
        }

        // Salary filtering - simplified to only check if min salary is >= the filter value
        if (salaryRange && salaryRange[0] > 0) {
          const minSalary = salaryRange[0];
          
          // Only filter for jobs where the min salary meets or exceeds the requested minimum
          query = query.gte('salary_range_min', minSalary);
        }

        // Sort and paginate
        query = query
          .order('created_at', { ascending: false })
          .range(from, to);

        const { data, error, count } = await query;
        
        if (error) {
          console.error('Error fetching jobs:', error);
          throw error;
        }

        console.log('[useJobs] Query returned', data?.length, 'results out of', count, 'total');
        
        if (data && data.length > 0) {
          console.log('[useJobs] First item publish_to_job_board:', data[0].publish_to_job_board);
          console.log('[useJobs] First item status:', data[0].status);
        }

        // Map the data to match existing Job interface
        const mappedJobs = data.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          location: item.location,
          key_skills: item.key_skills,
          work_presence: item.work_presence || 'Fully On-site',
          work_arrangement: item.work_arrangement || 'Full time',
          salary_range_min: item.salary_range_min,
          salary_range_max: item.salary_range_max,
          salary_currency: item.salary_currency || 'INR',
          industry: item.industry,
          custom_industry: item.custom_industry,
          seniority: item.seniority,
          created_at: item.created_at,
          status: item.status,
          applicants_count: item.applicants_count || 0,
          organization: {
            id: item.creator_profile_id,
            organization: item.creator_organization,
            full_name: item.creator_full_name || undefined, // Will be null in public view for security
            logo_url: item.creator_logo_url
          }
        })) as Job[];

        return {
          jobs: mappedJobs,
          totalCount: count ?? 0
        };
      } catch (err) {
        console.error('Error in jobs query:', err);
        throw err;
      }
    },
    enabled: true,
    staleTime: 30000,
    refetchOnMount: true
  });

  if (error) {
    console.error('Error in useJobs hook:', error);
  }

  return { 
    jobs: jobsResponse?.jobs || [], 
    totalCount: jobsResponse?.totalCount || 0, 
    isLoading, 
    profile, 
    error 
  };
}
